import { useState } from "react";
import {
  Control,
  Controller,
  FieldValues,
  Path,
  RegisterOptions,
} from "react-hook-form";
import { Eye, EyeOff } from "lucide-react";

interface PasswordInputProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  label: string;
  placeholder: string;
  rules?: RegisterOptions;
  className?: string;
}

const PasswordInput = <T extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  rules,
  className,
}: PasswordInputProps<T>) => {
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className={`flex flex-col ${className}`}>
      {label && (
        <label className="field-label">
          {label}
          {rules?.required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        <Controller
          control={control}
          name={name}
          rules={rules as any}
          render={({ field, fieldState: { error } }) => (
            <>
              <input
                {...field}
                autoComplete="new-password"
                type={showPassword ? "text" : "password"}
                placeholder={placeholder}
                className={`w-full rounded border p-3  ${
                  error ? "border-red-500" : "border-gray-300"
                }`}
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className={`absolute right-3 ${
                  error ? "top-[35%]" : "top-1/2"
                } -translate-y-1/2 text-gray-500 hover:text-gray-700`}
                tabIndex={-1}
              >
                {showPassword ? <Eye size={18} /> : <EyeOff size={18} />}
              </button>
              {error && (
                <div className="text-red-500 text-sm mt-1">{error.message}</div>
              )}
            </>
          )}
        />
      </div>
    </div>
  );
};

export default PasswordInput;
